/**
 * SubunitPlacementPreview — Ghost mesh following cursor during subunit placement
 *
 * Renders a semi-transparent preview box at the cursor position.
 * Uses category color for the ghost material.
 * Follows ObstaclePlacementPreview pattern.
 */

import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useSubunitStore } from '@/stores/subunitStore'
import { SUBUNIT_CATEGORY_COLORS } from '@/types/subunit'
import { FEET_TO_METERS } from '@/constants/units'

const GHOST_OPACITY = 0.35

interface SubunitPlacementPreviewProps {
  scene: THREE.Scene | null
  map: mapboxgl.Map | null
}

export function SubunitPlacementPreview({ scene, map }: SubunitPlacementPreviewProps) {
  const creatingDefaults = useSubunitStore((s) => s.creatingDefaults)
  const placingDefaults = useSubunitStore((s) => s.placingDefaults)
  const previewPosition = useSubunitStore((s) => s.previewPosition)
  const meshRef = useRef<THREE.Mesh | null>(null)
  const wireRef = useRef<THREE.LineSegments | null>(null)

  // Unified dimension source: placingDefaults (panel mode) OR creatingDefaults (view-mode)
  const dims = placingDefaults ?? creatingDefaults

  // Create/update mesh when dimensions change
  useEffect(() => {
    if (!scene || !dims) return

    // Clean up previous
    if (meshRef.current) {
      scene.remove(meshRef.current)
      meshRef.current.geometry.dispose()
      ;(meshRef.current.material as THREE.Material).dispose()
      meshRef.current = null
    }
    if (wireRef.current) {
      scene.remove(wireRef.current)
      wireRef.current.geometry.dispose()
      ;(wireRef.current.material as THREE.Material).dispose()
      wireRef.current = null
    }

    const w = dims.width * FEET_TO_METERS
    const d = dims.depth * FEET_TO_METERS
    const h = dims.height * FEET_TO_METERS
    const geometry = new THREE.BoxGeometry(w, h, d)

    const ghostColor = new THREE.Color(dims.color || SUBUNIT_CATEGORY_COLORS[dims.category])

    const material = new THREE.MeshStandardMaterial({
      color: ghostColor,
      transparent: true,
      opacity: GHOST_OPACITY,
      depthWrite: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = 'subunit-placement-preview'
    mesh.visible = false
    scene.add(mesh)
    meshRef.current = mesh

    // Wireframe overlay for visibility
    const edgesGeo = new THREE.EdgesGeometry(geometry)
    const edgesMat = new THREE.LineBasicMaterial({ color: ghostColor, transparent: true, opacity: 0.7 })
    const wireframe = new THREE.LineSegments(edgesGeo, edgesMat)
    wireframe.name = 'subunit-placement-preview-wire'
    wireframe.visible = false
    scene.add(wireframe)
    wireRef.current = wireframe

    return () => {
      if (meshRef.current) {
        scene.remove(meshRef.current)
        meshRef.current.geometry.dispose()
        ;(meshRef.current.material as THREE.Material).dispose()
        meshRef.current = null
      }
      if (wireRef.current) {
        scene.remove(wireRef.current)
        wireRef.current.geometry.dispose()
        ;(wireRef.current.material as THREE.Material).dispose()
        wireRef.current = null
      }
    }
  }, [scene, dims])

  // Update position when cursor moves
  useEffect(() => {
    if (!meshRef.current || !wireRef.current || !dims) return

    if (!previewPosition) {
      meshRef.current.visible = false
      wireRef.current.visible = false
      return
    }

    const xMeters = previewPosition.x * FEET_TO_METERS
    const zMeters = previewPosition.z * FEET_TO_METERS
    const elevation = dims.elevationFt * FEET_TO_METERS
    const halfHeight = (dims.height * FEET_TO_METERS) / 2

    meshRef.current.position.set(xMeters, elevation + halfHeight, zMeters)
    meshRef.current.visible = true

    wireRef.current.position.set(xMeters, elevation + halfHeight, zMeters)
    wireRef.current.visible = true

    map?.triggerRepaint()
  }, [previewPosition, dims, map])

  return null
}
